<template>
    <div class="half-menu" :class="{ 'no-hover': !isStylePreview && !isPreview, 'inline-edit-mode': enableInlineEdit }" :style="densityVars">
        <div class="wrap">
            <div class="title-card" :style="titleCardStyles">
                <h1
                    ref="titleTextRef"
                    v-show="true"
                    class="splash-title editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="titleStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateTitle"
                    @input="(e) => handleTextFieldInput('titleText', e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displayTitleText }}</h1>
                <div v-if="!isTitleHidden() && !isSubtextHidden()" class="title-divider" :style="titleDividerStyles"></div>
                <p
                    ref="subtextRef"
                    v-show="true"
                    class="body editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="bodyStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateSubtext"
                    @input="(e) => handleTextFieldInput('subtext', e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displaySubtext }}</p>
            </div>

            <div class="buttons" :style="buttonsGridStyles">
                <div
                    v-for="(btn, i) in filledButtons"
                    :key="i"
                    v-show="!btn._ghost && btn.enabled !== false"
                    class="btn editable-text"
                    :class="{
                        'is-editable': enableInlineEdit && !btn._ghost,
                        'btn-disabled': btn.enabled === false
                    }"
                    :style="getButtonStyles(btn)"
                    :ref="`buttonRef${i}`"
                    :contenteditable="enableInlineEdit && !btn._ghost && btn.enabled !== false"
                    @blur="(e) => updateButton(i, e)"
                    @input="(e) => handleTextFieldInput(`buttonText${i}`, e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ getDisplayButtonText(i) }}
                </div>
            </div>

            <div class="back-row">
                <div v-if="showBackButton" class="back-btn" :style="backBtnStyles">
                    <div class="back-icon-wrapper" :style="backIconStyles">
                        <component :is="icons.back2" class="back-icon" :color="backIconColor" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import icons from './menu-icons';
import menuMixin from './menu-mixin';
import menuReactivityMixin from './menu-reactivity-mixin';
const Back2SVG = require('!raw-loader!../assets/icons/back2.svg');

export default {
    name: "MenuTwoToFourOptionHalfScreen",
    mixins: [menuMixin, menuReactivityMixin],
    props: {
        styleData: {type: Object, default: () => ({})},
        menuData: {type: Object, default: () => ({})},
        isStylePreview: {type: Boolean, default: false},
        isPreview: {type: Boolean, default: false},
        enableInlineEdit: {type: Boolean, default: false}
    },
    data() {
        return {
            icons: icons
        };
    },
    computed: {
        titleText() {
            return this.cleanText(this.menuData.titleText);
        },
        displayTitleText() {
            const text = this.titleText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "Menu Title Goes Here";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        subtext() {
            return this.cleanText(this.menuData.subtext);
        },
        displaySubtext() {
            const text = this.subtext;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "This is where your menu description text will appear. You can provide instructions, information, or any other content you want to display to users.";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        buttons() {
            let buttons = [];
            if (this.menuData.buttons && this.menuData.buttons.length > 0) {
                buttons = this.menuData.buttons;
            } else {
                buttons = [
                    {text: "", enabled: true},
                    {text: "", enabled: true},
                    {text: "", enabled: false},
                    {text: "", enabled: false}
                ];
            }

            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                const defaultTexts = ["Button 1", "Button 2", "Button 3", "Button 4"];
                return buttons.map((btn, index) => {
                    const trimmedText = (btn.text && btn.text.trim()) || "";
                    return {
                        ...btn,
                        text: trimmedText || (btn.enabled !== false ? defaultTexts[index] : "")
                    };
                });
            }

            return buttons;
        },
        showBackButton() {
            return this.menuData.backButton?.enabled !== false;
        },
        filledButtons() {
            const out = [...this.buttons];
            while (out.length < 4) out.push({_ghost: true, text: ""});
            return out;
        },
        visibleButtonTexts() {
            if (!this.filledButtons || !Array.isArray(this.filledButtons)) {
                return [];
            }
            return this.filledButtons.map(btn => {
                if (btn._ghost) return '';
                const cleaned = this.cleanText(btn?.text);
                return cleaned || (this.enableInlineEdit ? ' ' : '');
            });
        },

        densityVars() {
            if (!this.isStylePreview) return {};
            return {
                "--base-font": "0.85rem",
                "--title-scale": "1.6",
                "--body-scale": "1.1",
                "--btn-scale": "1.0",
                "--element-scale": "0.7",
                "--gap-scale": "0.6"
            };
        },

        effective() {
            const d = {
                titleScreenBackgroundColor: {r: 44, g: 62, b: 80, a: 1},
                titleColor: {r: 255, g: 255, b: 255, a: 1},
                textColor: {r: 235, g: 244, b: 248, a: 0.95},
                buttonBackgroundColor: {r: 33, g: 122, b: 145, a: 0.95},
                buttonHoverColor: {r: 52, g: 192, b: 32, a: 1},
                buttonTextColor: {r: 255, g: 255, b: 255, a: 1},
                outlineColor: {r: 255, g: 255, b: 255, a: 0.9},
                opacity: 1.0
            };
            return {...d, ...this.styleData};
        },

        buttonsGridStyles() {
            const gap = this.isStylePreview ? "calc(10px * var(--gap-scale, 1)) calc(12px * var(--gap-scale, 1))" : "14px 18px";
            return {
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap,
                width: "100%"
            };
        },

        titleCardStyles() {
            const pad = this.isStylePreview ? "calc(12px * var(--element-scale, 1)) calc(14px * var(--element-scale, 1))" : "16px 18px";
            const radius = this.isStylePreview ? "calc(10px * var(--element-scale, 1))" : "14px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";
            return {
                padding: pad,
                borderRadius: radius,
                border: `${borderWidth} solid`,
                backgroundColor: this.rgba(this.effective.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effective.outlineColor, false),
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)"
            };
        },
        titleStyles() {
            const lineHeight = 1.22;
            const fontSize = "calc(var(--base-font) * var(--title-scale))";
            const marginBottom = this.isStylePreview ? "calc(12px * var(--gap-scale, 1))" : "12px";
            const hidden = this.isTitleHidden();
            return {
                margin: `0 0 ${marginBottom} 0`,
                fontWeight: 800,
                fontSize: fontSize,
                lineHeight: lineHeight,
                letterSpacing: "0.4px",
                color: this.rgba(this.effective.titleColor, false),
                width: "100%",
                minWidth: "100%",
                maxWidth: "100%",
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },
        bodyStyles() {
            const lineHeight = 1.6;
            const fontSize = "calc(var(--base-font) * var(--body-scale))";
            const lines = this.isStylePreview ? 4 : 7;
            const hidden = this.isSubtextHidden();
            return {
                margin: 0,
                fontSize: fontSize,
                lineHeight: lineHeight,
                color: this.rgba(this.effective.textColor, false),
                width: "100%",
                minWidth: "100%",
                maxWidth: "100%",
                minHeight: `calc(${fontSize} * ${lineHeight} * ${lines})`,
                maxHeight: `calc(${fontSize} * ${lineHeight} * ${lines})`,
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },
        titleDividerStyles() {
            const height = this.isStylePreview ? "calc(2px * var(--element-scale, 1))" : "2px";
            const marginBottom = this.isStylePreview ? "calc(12px * var(--gap-scale, 1))" : "12px";
            return {
                width: '100%',
                height: height,
                backgroundColor: this.rgba(this.effective.titleColor, false),
                margin: `0 0 ${marginBottom} 0`
            };
        },
        backBtnStyles() {
            const w = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "75px";
            const h = this.isStylePreview ? "calc(44px * var(--element-scale, 1))" : "56px";
            const fontSize = this.isStylePreview ? "calc(24px * var(--element-scale, 1))" : "24px";
            const borderRadius = this.isStylePreview ? "calc(10px * var(--element-scale, 1))" : "12px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";
            return {
                width: w,
                height: h,
                borderRadius: borderRadius,
                border: `${borderWidth} solid`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: fontSize,
                fontWeight: "bold",
                cursor: "pointer",
                boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
                transition: 'background-color .18s ease, box-shadow .18s ease',
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                color: this.rgba(this.effective.textColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                '--hover-bg': this.rgba(this.effective.buttonHoverColor)
            };
        },
        backIconStyles() {
            return this.getIconStylesFromSvg(Back2SVG);
        },
        backIconColor() {
            const effective = this.getEffectiveStyle();
            return this.rgba(effective.textColor, false);
        }
    },
    methods: {
        getButtonStyles(btn) {
            const minH = this.isStylePreview ? "calc(48px * var(--element-scale, 1))" : "58px";
            const pad = this.isStylePreview ? "calc(8px * var(--element-scale, 1)) calc(10px * var(--element-scale, 1))" : "10px 12px";
            const radius = this.isStylePreview ? "calc(10px * var(--element-scale, 1))" : "12px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";

            const hidden = btn?._ghost || this.isButtonHidden(btn);
            if (hidden) {
                return {minHeight: minH, borderRadius: radius, visibility: "hidden", pointerEvents: "none"};
            }

            return {
                minHeight: minH,
                padding: pad,
                borderRadius: radius,
                border: `${borderWidth} solid`,
                fontSize: "calc(var(--base-font) * var(--btn-scale))",
                fontWeight: 600,
                letterSpacing: "0.2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                wordBreak: "break-word",
                whiteSpace: "normal",
                textAlign: "center",
                cursor: "pointer",
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                color: this.rgba(this.effective.buttonTextColor, false),
                WebkitTextFillColor: this.rgba(this.effective.buttonTextColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                textShadow: "0 1px 0 rgba(0,0,0,0.35)",
                transition: "background-color .18s ease, box-shadow .18s ease, transform .18s ease",
                "--hover-bg": this.rgba(this.effective.buttonHoverColor)
            };
        },

        getDisplayButtonText(index) {
            return this.visibleButtonTexts[index] || '';
        },
        updateTitle(event) {
            this.updateTextField('titleText', event);
        },

        updateSubtext(event) {
            this.updateTextField('subtext', event);
        },

        updateButton(index, event) {
            const btn = this.filledButtons[index];
            if (btn._ghost) return;
            this.updateButtonText(index, event, 'buttons');
        },

        isTitleHidden() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.titleText);
        },

        isSubtextHidden() {
            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.subtext);
        },

        isButtonHidden(btn) {
            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return false;
            }
            if (btn.enabled === false || !btn.text || (typeof btn.text === 'string' && btn.text.trim().length === 0)) {
                return true;
            }
            return false;
        }
    },
    watch: {
        displayTitleText(newVal) {
            this.syncEditableText('titleTextRef', newVal);
        },
        displaySubtext(newVal) {
            this.syncEditableText('subtextRef', newVal);
        },
        visibleButtonTexts: {
            handler(newTexts, oldTexts) {
                if (this.enableInlineEdit && Array.isArray(newTexts)) {
                    this.$nextTick(() => {
                        newTexts.forEach((displayText, index) => {
                            const btn = this.filledButtons[index];
                            if (btn && !btn._ghost && btn.enabled !== false) {
                                if (!oldTexts || oldTexts[index] !== displayText) {
                                    this.syncButtonTextContent(index, displayText);
                                }
                            }
                        });
                    });
                }
            }
        }
    }
};
</script>

<style scoped>
.half-menu {
    width: 100%;
    height: 100%;
    background: transparent;
    box-sizing: border-box;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    --base-font: 1rem;
    --title-scale: 2.0;
    --body-scale: 1.05;
    --btn-scale: 1.0;
    --element-scale: 1.0;
    --gap-scale: 1.0;
}
.wrap {
    width: min(540px, 50%);
    display: grid;
    grid-template-rows: auto auto auto;
    row-gap: 14px;
    box-sizing: border-box;
    overflow: hidden;
    flex-shrink: 0;
}

.meeting-workspace-container .wrap {
    width: 30vw !important;
    min-width: 30vw !important;
    max-width: 30vw !important;
}

.title-card {
    box-sizing: border-box;
    overflow: hidden;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
}

.buttons {
    box-sizing: border-box;
    width: 100%;
    min-width: 100%;
}

.splash-title {
    display: block;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
}

.back-row {
    display: flex;
    justify-content: center;
    margin-top: 6px;
}

.half-menu .btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.half-menu .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.half-menu:not(.no-hover) .btn:hover {
    background-color: var(--hover-bg) !important;
}

.half-menu .back-btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.half-menu .back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.half-menu:not(.no-hover) .back-btn:hover {
    background-color: var(--hover-bg) !important;
}


@media (max-width: 640px) {
    .wrap {
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
    }

    .buttons {
        grid-template-columns: 1fr !important;
    }
}

.editable-text.is-editable {
    cursor: text;
    position: relative;
    outline: none;
}

.editable-text.is-editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.4);
}

.editable-text.is-editable:focus {
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.7);
}

.splash-title {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
}

.body {
    display: -webkit-box !important;
    -webkit-line-clamp: 7 !important;
    line-clamp: 7 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
}

.btn {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    word-break: break-word !important;
    white-space: normal !important;
    min-height: 64px !important;
    max-height: none !important;
    box-sizing: border-box !important;
}

.btn .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.btn[contenteditable="true"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.btn[contenteditable="true"] .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.btn-disabled {
    visibility: hidden !important;
    pointer-events: none !important;
}

.back-icon-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.back-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}
</style>
